import { Controller } from "@hotwired/stimulus";
import Dropzone from "dropzone";
import { Turbo } from "@hotwired/turbo-rails";

Dropzone.autoDiscover = false;

export default class extends Controller {
  static targets = [
    "input",
    "preview",
    "form",
    "dropzone",
    "fileSelect",
    "attachmentIds",
    "attachmentLabel",
  ];
  static values = {
    maxFilesize: { type: Number, default: 40 },
    acceptedFiles: { type: String, default: ".jpeg,.jpg,.png,.gif,.pdf,.csv,.xlsx,.docx,.tiff,.tif" },
    maxFiles: { type: Number, default: 256 },
    uploadUrl: { type: String, default: "/attachments" },
    existingAttachments: { type: Array, default: [] },
  }

  connect() {
    this.initializeDropzone();
    this.loadExistingAttachments()
  }

  getDefaultPreviewTemplate() {
    return `
      <div class="dz-preview dz-file-preview">
        <div class="dz-image">
          <img data-dz-thumbnail />
        </div>
        <div class="dz-details">
          <div class="dz-size" data-dz-size></div>
          <div class="dz-filename"><span data-dz-name></span></div>
        </div>
        <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
        <div class="dz-error-message"><span data-dz-errormessage></span></div>
        <div class="dz-success-mark"><span>✔</span></div>
        <div class="dz-error-mark"><span>✘</span></div>
      </div>
    `;
  }

  initializeDropzone() {
    this.dropzone = new Dropzone(this.element, {
      url: this.uploadUrlValue,
      maxFilesize: this.maxFilesizeValue,
      acceptedFiles: this.acceptedFilesValue,
      maxFiles: this.maxFilesValue,
      autoProcessQueue: true,
      addRemoveLinks: false,
      previewsContainer: this.previewTarget,
      paramName: "attachment[file]",
      previewTemplate: this.previewTemplate || this.getDefaultPreviewTemplate(),
      clickable: this.fileSelectTarget,
      createImageThumbnails: true,
      thumbnailWidth: 80,
      thumbnailHeight: 80,
      headers: {
        "X-CSRF-Token": this.getCsrfToken(),
      },
      sending: (file, xhr, formData) => {
        formData.append("attachment[name]", file.name);
      },
      init: () => {
        this.element.addEventListener("dragenter", () => this.showDropzone());
        this.element.addEventListener("dragleave", (e) => {
          if (e.target === this.dropzoneTarget) {
            this.hideDropzone();
          }
        });
        this.element.addEventListener("drop", () => this.hideDropzone());
      },
    });

    this.dropzone.on("addedfile", (file) => {
      this.fileAdded(file);
      // Manually set file name since custom template might not populate it
      const nameElement = file.previewElement.querySelector("[data-dz-name]");
      if (nameElement) {
        nameElement.textContent = file.name;
      }
      const removeBtn = file.previewElement.querySelector(".dz-remove");
      if (removeBtn) {
        removeBtn.addEventListener("click", (e) => {
          e.preventDefault();
          this.fileRemoved(file);
          this.dropzone.removeFile(file);
        });
      }
    });
    this.dropzone.on("success", (file, response) => {
      this.fileUploaded(file, response);
    });
    this.dropzone.on("error", (file, errorMessage) => {
      this.fileError(file, errorMessage);
    });
    this.dropzone.on("maxfilesexceeded", (file) => {
      alert(`Cannot add ${file.name}: Maximum number of files exceeded`);
      this.dropzone.removeFile(file);
    });
  }

  showDropzone() {
    this.dropzoneTarget.classList.remove("d-none");
    this.dropzoneTarget.classList.add("d-flex");
  }

  hideDropzone() {
    this.dropzoneTarget.classList.remove("d-flex");
    this.dropzoneTarget.classList.add("d-none");
  }

  fileAdded(file) {
    this.showPreview();
    this.hideDropzone();
    this.updateAttachmentCount();
    this.setFileIcon(file);
    this.formatFileSize(file);
  }

  fileRemoved(file) {
    // Remove from uploaded attachments if it was successfully uploaded
    if (file.attachmentId) {
      this.removeUploadedAttachment(file.attachmentId);
    }

    if (this.dropzone.files.length === 0) {
      this.hidePreview();
    }
    this.updateAttachmentCount();
  }

  fileUploaded(file, response) {
    if (response.success) {
      file.attachmentId = response.id;
      this.uploadedAttachments.push(response.id);
      this.updateAttachmentIdsInput();

      // Update file size display with formatted size
      const sizeElement = file.previewElement.querySelector(".dz-size");
      if (sizeElement && response.size) {
        sizeElement.textContent = `${response.size} MB`;
      }

      // Show success indicator
      const successMark = file.previewElement.querySelector(".dz-success-mark");
      if (successMark) {
        successMark.classList.remove("d-none");
      }
    } else {
      this.fileError(file, response.errors);
    }
  }

  fileError(file, errorMessage) {
    console.error("Upload error:", errorMessage);
    alert(
      `Error uploading ${file.name}: ${Array.isArray(errorMessage) ? errorMessage.join(", ") : errorMessage}`,
    );
    this.dropzone.removeFile(file);
  }

  async removeUploadedAttachment(attachmentId) {
    try {
      const response = await fetch(`/attachments/${attachmentId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this.getCsrfToken(),
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        this.uploadedAttachments = this.uploadedAttachments.filter(
          (id) => id !== attachmentId,
        );
        this.updateAttachmentIdsInput();
      }
    } catch (error) {
      console.error("Error deleting attachment:", error);
    }
  }

  updateAttachmentIdsInput() {
    if (this.hasAttachmentIdsTarget) {
      // Get the field name from the existing hidden field
      const fieldName = this.attachmentIdsTarget.name;
      const arrayFieldName = fieldName.replace(/\[\]$/, '') + '[]';
      const container = this.attachmentIdsTarget.parentNode;

      // Clear any existing attachment input fields
      const existingFields = container.querySelectorAll(`input[name="${arrayFieldName}"]`);
      existingFields.forEach(field => field.remove());

      // Create individual hidden fields for each attachment
      this.uploadedAttachments.forEach(attachmentId => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = arrayFieldName;
        input.value = attachmentId;
        container.appendChild(input);
      });
    }
  }

  clearAttachments() {
    this.uploadedAttachments = [];
    this.updateAttachmentIdsInput();
    this.updateAttachmentCount();
  }

  updateAttachmentCount() {
    const attachmentCount = this.dropzone.files.length;
    const attachmentLabel = this.element.querySelector(
      '[data-dropzone-target="attachmentLabel"]',
    );

    if (attachmentLabel) {
      if (attachmentCount === 0) {
        attachmentLabel.style.display = "none";
      } else {
        attachmentLabel.style.display = "block";
        attachmentLabel.textContent = `Attachments (${attachmentCount})`;
      }
    }
  }

  loadExistingAttachments() {
    this.uploadedAttachments = []
    if (this.existingAttachmentsValue !== undefined && this.existingAttachmentsValue.length > 0) {
      this.existingAttachmentsValue.forEach(attachment => {
        // Add to uploaded attachments array
        this.uploadedAttachments.push(attachment.id);

        // Create a mock file object for dropzone
        const mockFile = {
          name: attachment.name,
          size: attachment.size,
          attachmentId: attachment.id,
          status: Dropzone.SUCCESS
        };

        // Add the file to dropzone without uploading
        this.dropzone.emit("addedfile", mockFile);
        this.dropzone.emit("complete", mockFile);

        // Handle image thumbnails for existing attachments
        const fileExtension = attachment.name.split(".").pop().toLowerCase();
        if (this.isImageFile(fileExtension)) {
          // For existing images, set the thumbnail manually
          const thumbnailElement = mockFile.previewElement.querySelector("[data-dz-thumbnail]");
          const iconElement = mockFile.previewElement.querySelector(".dz-file-icon");

          if (thumbnailElement && attachment.url) {
            thumbnailElement.src = attachment.url;
            thumbnailElement.classList.remove("d-none");
            if (iconElement) {
              iconElement.style.display = "none";
            }
          }
        } else {
          // For non-images, set the appropriate icon
          this.setFileIcon(mockFile);
        }

        // Update the preview to show it's already uploaded
        const successMark = mockFile.previewElement.querySelector(".dz-success-mark");
        const progressBar = mockFile.previewElement.querySelector(".dz-progress");
        if (successMark) {
          successMark.classList.remove("d-none");
        }
        if (progressBar) {
          progressBar.style.display = "none";
        }

        // Format file size
        this.formatFileSize(mockFile);

        // Add the file to dropzone's files array
        this.dropzone.files.push(mockFile);
      });

      // Update the UI
      this.showPreview();
      this.updateAttachmentCount();
      this.updateAttachmentIdsInput();
    }
  }


  // Shared file icon mapping
  getFileIcon(extension) {
    const iconMap = {
      pdf: "fa fa-file-pdf text-danger",
      doc: "fa fa-file-word text-primary",
      docx: "fa fa-file-word text-primary",
      xls: "fa fa-file-excel text-success",
      xlsx: "fa fa-file-excel text-success",
      csv: "fa fa-file-csv text-info",
      txt: "fa fa-file-text text-secondary",
      default: "fa fa-file text-muted",
    };

    return iconMap[extension] || iconMap["default"];
  }

  // Shared file icon mapping (simplified for replacement)
  getFileIconForExtension(ext) {
    const iconMap = {
      pdf: "fa-file-pdf text-danger",
      csv: "fa-file-csv text-info",
      xlsx: "fa-file-excel text-success",
      docx: "fa-file-word text-primary",
      default: "fa-file text-muted",
    };
    return iconMap[ext] || iconMap["default"];
  }

  // Shared image file detection
  isImageFile(extension) {
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "tiff", "tif"];
    return imageExtensions.includes(extension);
  }

  // Shared file validation
  validateFile(file) {
    // Check file size (convert MB to bytes)
    if (file.size > this.maxFilesizeValue * 1024 * 1024) {
      alert(`File ${file.name} is too large. Maximum size is ${this.maxFilesizeValue}MB.`);
      return false;
    }

    // Check file type
    const extension = "." + file.name.split(".").pop().toLowerCase();
    const acceptedExtensions = this.acceptedFilesValue.split(",");
    if (!acceptedExtensions.includes(extension)) {
      alert(`File type ${extension} is not allowed.`);
      return false;
    }

    return true;
  }

  // Shared drag and drop event handlers
  handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  // Shared file size formatting
  formatFileSize(file) {
    if (typeof file === 'number') {
      // If file is just a size in bytes, format it
      const sizeInMB = (file / (1024 * 1024)).toFixed(2);
      return `${sizeInMB} MB`;
    }

    // Otherwise, it's a file object with preview element
    const sizeElement = file.previewElement.querySelector(".dz-size");
    if (sizeElement && file.size) {
      const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
      sizeElement.textContent = `${sizeInMB} MB`;
    }

    // Ensure file name is set
    const nameElement = file.previewElement.querySelector("[data-dz-name]");
    if (nameElement && file.name) {
      nameElement.textContent = file.name;
    }
  }

  // Shared CSRF token helper
  getCsrfToken() {
    return document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute("content");
  }

  setFileIcon(file) {
    const fileExtension = file.name.split(".").pop().toLowerCase();
    const iconElement = file.previewElement.querySelector(".dz-file-icon");
    const thumbnailElement = file.previewElement.querySelector("[data-dz-thumbnail]");
    const fileContentDiv = file.previewElement.querySelector(".dz-file-content");
    const overlayElement = file.previewElement.querySelector(".attachment-hover-overlay");

    if (this.isImageFile(fileExtension)) {
      // For images: show thumbnail filling square, hide file content, enable hover overlay
      if (thumbnailElement) thumbnailElement.classList.remove("d-none");
      if (fileContentDiv) fileContentDiv.classList.add("d-none");
      file.previewElement.classList.add("attachment-hover-container");
      
      // Populate overlay content for images
      const nameOverlay = file.previewElement.querySelector("[data-dz-name-overlay]");
      const sizeOverlay = file.previewElement.querySelector("[data-dz-size-overlay]");
      if (nameOverlay) nameOverlay.textContent = file.name;
      if (sizeOverlay) {
        const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
        sizeOverlay.textContent = `${sizeInMB}MB`;
      }
    } else {
      // For non-images: hide thumbnail, show file content with icon/name/size
      if (thumbnailElement) thumbnailElement.classList.add("d-none");
      if (fileContentDiv) fileContentDiv.classList.remove("d-none");
      if (overlayElement) overlayElement.classList.add("d-none");
      file.previewElement.classList.remove("attachment-hover-container");

      const iconClass = this.getFileIcon(fileExtension);
      if (iconElement) iconElement.className = `dz-file-icon fa-3x ${iconClass}`;
    }
  }

  showPreview() {
    this.previewTarget.classList.remove("d-none");
  }

  hidePreview() {
    this.previewTarget.classList.add("d-none");
  }

  submitForm(event) {
    event.preventDefault();
    this.updateAttachmentIdsInput();
    this.formTarget.requestSubmit();
  }

  disconnect() {
    this.dropzone.destroy();
  }
}
